import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from 'lib/utils'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  loading?: boolean
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      loading = false,
      children,
      disabled,
      asChild = false,
      ...props
    },
    ref
  ) => {
    // When asChild is true, Slot requires exactly one child element
    // If children is not a single element, fall back to regular button
    let isValidAsChild = false
    let singleChild: React.ReactElement | null = null
    
    if (asChild && children !== null && children !== undefined) {
      const childrenArray = React.Children.toArray(children)
      if (childrenArray.length === 1 && React.isValidElement(childrenArray[0])) {
        isValidAsChild = true
        singleChild = childrenArray[0] as React.ReactElement
      }
    }
    
    // If asChild is requested but invalid, don't use Slot
    const shouldUseAsChild = isValidAsChild
    const Component = shouldUseAsChild ? Slot : 'button'

    // Build className
    const buttonClassName = cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      {
        'bg-primary text-primary-foreground hover:bg-primary/90': variant === 'default',
        'bg-destructive text-destructive-foreground hover:bg-destructive/90': variant === 'destructive',
        'border border-input bg-background hover:bg-accent hover:text-accent-foreground': variant === 'outline',
        'bg-secondary text-secondary-foreground hover:bg-secondary/80': variant === 'secondary',
        'hover:bg-accent hover:text-accent-foreground': variant === 'ghost',
        'text-primary underline-offset-4 hover:underline': variant === 'link',
      },
      {
        'h-10 px-4 py-2': size === 'default',
        'h-9 rounded-md px-3': size === 'sm',
        'h-11 rounded-md px-8': size === 'lg',
        'h-10 w-10': size === 'icon',
      },
      className
    )

    // If using asChild with Slot, clone the single child with props
    if (shouldUseAsChild && singleChild) {
      return (
        <Slot
          className={buttonClassName}
          ref={ref}
          {...props}
        >
          {singleChild}
        </Slot>
      )
    }

    // Regular button rendering
    return (
      <Component
        className={buttonClassName}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        )}
        {children}
      </Component>
    )
  }
)
Button.displayName = 'Button'

export { Button }
